// biome-ignore-all lint/a11y/noInteractiveElementToNoninteractiveRole: explicit role="banner" required by UI-SPEC §6.2 acceptance gate
//   `<header>`'s implicit role is "banner" only at document root. When
//   composed inside a parent <main>/region (MatchScreen will do this in
//   W3), the implicit role disappears. UI-SPEC §6.2 mandates an explicit
//   `role="banner"` so the contract stays stable regardless of mount
//   context.
//
// Phase 17 Plan 04 Task 1 — MatchTopBar primitive (UI-SPEC §5.1 row 6).
//
// Match-screen header composing the Btn primitive (back + settings icon-only
// buttons) and a "turn-forward, identity-light" center block. Replaces the
// current 80-line apps/web/src/ui/match/TopBar.tsx in W3 (Plan 17-05 owns the
// wire-up).
//
// ARCA-586 (ARCA-595) redesign — one readable row at phone widths (360–430px):
//   - The inline opponent ELO is GONE (it was placeholder data and a second
//     number next to the real "Score N"; reclaiming its width is the biggest
//     space win — see parent plan D1).
//   - The raw status enum ("played"/"thinking") is replaced by a localized
//     turn indicator ("Your move" / "Thinking…") driven by the real
//     `opponentStatus`. It is the hero of the row and never truncates (D2).
//   - Opponent avatar + name stay as compact identity; the placeholder name is
//     the graceful-truncation point and hides below a small breakpoint while
//     the avatar + turn text persist (D3). Real name/ELO wiring is ARCA-126.
//
// POLISH-01 a11y resolution: back + settings buttons carry i18n aria-labels
// via useIntl/formatMessage with the keys `match.topbar.back` /
// `match.topbar.settings` (added en/sv to public/locales/{en,sv}.json in
// this plan, parity preserved).
//
// DOM (per UI-SPEC §6.2):
//   <header role="banner" className="match-topbar">
//     <Btn kind="ghost" size="sm">{<BackArrowIcon />}</Btn>
//     <div className="your-score">…</div>            // ARCA-152, when scored
//     <div className="opponent-strip">
//       <span className="initial">{opponentInitial}</span>
//       <span className="name">{opponentName}</span>
//       <span className="turn-indicator turn-yours|turn-opponent">{turn}</span>
//     </div>
//     {hintSlot}
//     <Btn kind="them" size="sm">{<SettingsCogIcon />}</Btn>
//   </header>

import type { JSX, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { Btn } from './Btn'

export interface MatchTopBarProps {
  readonly opponentName: string
  /**
   * Real, live turn signal from `PlayerView`. `'thinking'` = the opponent is
   * to move; `'played'` = they've moved and it's your turn. Drives the
   * localized turn indicator (ARCA-586 D2), never rendered as the raw enum.
   */
  readonly opponentStatus: 'thinking' | 'played'
  readonly opponentInitial?: string
  /**
   * ARCA-152 — the local player's running cumulative word score
   * (`PlayerView.your_score`). Rendered as a compact labelled "Score N" chip,
   * visually distinct from the opponent ELO so the two numbers are never
   * confused. Optional so callers/tests that don't surface a score (and the
   * generic primitive demos) render the unchanged top bar.
   */
  readonly yourScore?: number
  readonly onBackClick: () => void
  readonly onSettingsClick: () => void
  readonly ariaLabel?: string
  /**
   * Phase v1.3 (MUI-05 / D-COACH-01) — optional slot rendered between the
   * opponent strip and the settings cog. Consumer mounts `<HintButton>`
   * here; the slot is omitted when no node is passed so the existing
   * Plan 17-04 visual contract is preserved for screens that don't surface
   * coach hints.
   */
  readonly hintSlot?: ReactNode
}

function BackArrowIcon(): JSX.Element {
  // Folk back chevron (prototype ICON.back) — round-capped single stroke.
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 5l-7 7 7 7" />
    </svg>
  )
}

function SettingsCogIcon(): JSX.Element {
  // Folk gear (prototype ICON.gear) — stroke-drawn hub + eight spokes.
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.6M12 18.6v2.6M21.2 12h-2.6M5.4 12H2.8M18.5 5.5l-1.8 1.8M7.3 16.7l-1.8 1.8M18.5 18.5l-1.8-1.8M7.3 7.3L5.5 5.5" />
    </svg>
  )
}

export function MatchTopBar({
  opponentName,
  opponentStatus,
  opponentInitial,
  yourScore,
  onBackClick,
  onSettingsClick,
  ariaLabel,
  hintSlot,
}: MatchTopBarProps): JSX.Element {
  const intl = useIntl()
  const backLabel = intl.formatMessage({ id: 'match.topbar.back' })
  const settingsLabel = intl.formatMessage({ id: 'match.topbar.settings' })
  // ARCA-152: the local running score. role="img" + aria-label gives AT a
  // single unambiguous "Your score, N" announcement, while sighted users still
  // see the "Score" tag + the number.
  const scoreTag = intl.formatMessage({ id: 'match.topbar.your_score_label' })
  const yourScoreAria = intl.formatMessage(
    { id: 'match.topbar.your_score_aria' },
    { score: yourScore ?? 0 },
  )
  // ARCA-586 D2: the live turn indicator — the hero of the row. `'thinking'`
  // means the opponent is to move ("Thinking…"); otherwise it's your turn
  // ("Your move"). Always localized, never the raw enum, and the visible text
  // is the accessible announcement (no aria-hidden).
  const isOpponentTurn = opponentStatus === 'thinking'
  const turnText = intl.formatMessage({
    id: isOpponentTurn
      ? 'match.topbar.turn_opponent'
      : 'match.topbar.turn_yours',
  })

  return (
    <header
      role="banner"
      className="match-topbar"
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <Btn kind="ghost" size="sm" onClick={onBackClick} ariaLabel={backLabel}>
        <BackArrowIcon />
      </Btn>
      {yourScore !== undefined ? (
        <div
          className="your-score"
          role="img"
          aria-label={yourScoreAria}
          data-testid="match-your-score"
        >
          <span className="your-score-tag" aria-hidden="true">
            {scoreTag}
          </span>
          <span
            className="your-score-value"
            aria-hidden="true"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {yourScore}
          </span>
        </div>
      ) : null}
      <div className="opponent-strip">
        {opponentInitial ? (
          <span className="initial" aria-hidden="true">
            {opponentInitial}
          </span>
        ) : null}
        <span className="name" title={opponentName}>
          {opponentName}
        </span>
        <span
          className={`turn-indicator ${
            isOpponentTurn ? 'turn-opponent' : 'turn-yours'
          }`}
        >
          {turnText}
        </span>
      </div>
      {hintSlot}
      <Btn
        kind="them"
        size="sm"
        onClick={onSettingsClick}
        ariaLabel={settingsLabel}
      >
        <SettingsCogIcon />
      </Btn>
    </header>
  )
}
